import { useState, useEffect, useMemo, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import routes from "routes";
import API_BASE_URL from "config/api";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import brandWhite from "assets/images/logo-logicookie.png";
import brandDark from "assets/images/logo-logicookie-dark.png";
import brandIconLight from "assets/images/logo-logicookie-sm.svg";
import brandIconDark from "assets/images/logo-logicookie-sm-dark.svg";
import SignInIllustration from "layouts/authentication/sign-in/illustration";
import ResetIllustration from "layouts/authentication/reset-password/illustration";

// Auth kontrolü için yardımcı fonksiyon
const checkAuth = () => {
  return !!sessionStorage.getItem("authToken");
};

// Özel Route bileşeni
const ProtectedRoute = ({ element, ...rest }) => {
  const location = useLocation();
  return checkAuth() ? (
    element
  ) : (
    <Navigate to="/authentication/sign-in/illustration" state={{ from: location }} replace />
  );
};

const DEFAULT_ROLE = "user";

const normalizeRole = (role) =>
  typeof role === "string" && role.trim()
    ? role.trim().toLowerCase()
    : DEFAULT_ROLE;

const extractRoleFromRaw = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") return parsed;
    if (parsed && typeof parsed.role === "string") return parsed.role;
    if (parsed && typeof parsed.user?.role === "string") return parsed.user.role;
  } catch (e) {
    if (raw.trim()) return raw;
  }
  if (raw.trim()) return raw;
  return null;
};

const getCurrentUserRole = () => {
  if (typeof window === "undefined") return DEFAULT_ROLE;
  const directRole = extractRoleFromRaw(sessionStorage.getItem("userRole"));
  if (directRole) return normalizeRole(directRole);

  const userDataRole = extractRoleFromRaw(sessionStorage.getItem("userData"));
  if (userDataRole) return normalizeRole(userDataRole);

  try {
    const meRaw = sessionStorage.getItem("userMeData");
    if (meRaw) {
      const parsed = JSON.parse(meRaw);
      const role = parsed?.user?.role;
      if (typeof role === "string") return normalizeRole(role);
    }
  } catch (e) {
    // ignore parse errors
  }

  return DEFAULT_ROLE;
};

const filterRoutesByRole = (list, role) => {
  if (!Array.isArray(list)) return [];
  return list.reduce((acc, item) => {
    if (item.roles && !item.roles.includes(role)) {
      return acc;
    }

    const filteredItem = { ...item };

    if (item.collapse) {
      const filteredCollapse = filterRoutesByRole(item.collapse, role);
      if (filteredCollapse.length === 0) {
        if (!item.route) {
          return acc;
        }
        delete filteredItem.collapse;
      } else {
        filteredItem.collapse = filteredCollapse;
      }
    }

    acc.push(filteredItem);
    return acc;
  }, []);
};

const App = () => {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const roleFetchAttemptedRef = useRef(false);

  // Viewport tracking for popup logic (shows popup for all widths below 1200)
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1200 : false
  );
  useEffect(() => {
    function onResize() {
      setIsNarrow(window.innerWidth < 1200);
    }
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close expanded sidenav on outside click (only when width < 1200)
  useEffect(() => {
    function handleDocClick(e) {
      if (!isNarrow) return;               // only 1000–1199
      if (miniSidenav) return;             // only when expanded (mini=false)
      const withinDrawer = e.target.closest(".MuiDrawer-paper");
      const onPopupBtn  = e.target.closest("#sidenav-popup-button");
      if (!withinDrawer && !onPopupBtn) {
        setMiniSidenav(dispatch, true);    // collapse back to mini
      }
    }
    document.addEventListener("click", handleDocClick, true);
    return () => document.removeEventListener("click", handleDocClick, true);
  }, [isNarrow, miniSidenav, dispatch]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    setUserRole(getCurrentUserRole());
  }, [pathname]);

  useEffect(() => {
    const handleStorage = () => setUserRole(getCurrentUserRole());
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const token = sessionStorage.getItem("authToken");
    if (!token) {
      roleFetchAttemptedRef.current = false;
      return undefined;
    }

    const storedRole = getCurrentUserRole();
    if (storedRole && storedRole !== userRole) {
      setUserRole(storedRole);
    }

    if (storedRole && storedRole !== DEFAULT_ROLE) {
      return undefined;
    }

    if (roleFetchAttemptedRef.current) {
      return undefined;
    }
    roleFetchAttemptedRef.current = true;

    let isCancelled = false;

    const fetchRole = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const fetchedRole = normalizeRole(data?.user?.role);

        if (!fetchedRole || isCancelled) {
          return;
        }

        try {
          sessionStorage.setItem("userMeData", JSON.stringify(data));
        } catch (e) {
          /* ignore serialization issues */
        }
        sessionStorage.setItem("userRole", fetchedRole);
        if (fetchedRole !== userRole) {
          setUserRole(fetchedRole);
        }
      } catch (error) {
        // silently fail, menu will default to basic role
      }
    };

    fetchRole();

    return () => {
      isCancelled = true;
    };
  }, [userRole, pathname]);

  const filteredRoutes = useMemo(() => filterRoutesByRole(routes, userRole), [userRole]);

  // Route oluşturma fonksiyonu (auth kontrolü eklenmiş)
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route && route.roles && !route.roles.includes(userRole)) {
        return null;
      }
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        // Auth gerektirmeyen sayfalar (login, signup vb.)
        const isAuthPage = route.route.startsWith("/sign-in");
        
        return (
          <Route
            exact
            path={route.route}
            element={
              isAuthPage ? (
                route.component
              ) : (
                <ProtectedRoute element={route.component} />
              )
            }
            key={route.key}
          />
        );
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && checkAuth() && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandIcon={(transparentSidenav && !darkMode) || whiteSidenav ? brandIconDark : brandIconLight}
              brandName=""
              routes={filteredRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            {isNarrow && miniSidenav && (
              <MDBox
                sx={{
                  position: "fixed",
                  right: 16,
                  bottom: 16,
                  zIndex: (theme) => theme.zIndex.modal + 1,
                }}
              >
                <IconButton
                  id="sidenav-popup-button"
                  onClick={() => setMiniSidenav(dispatch, false)}
                  size="large"
                  sx={{
                    bgcolor: "#bababa",
                    color: "black",
                    boxShadow: 3,
                    "&:hover": { bgcolor: "#a0a0a0" },
                  }}
                >
                  <Icon>menu</Icon>
                </IconButton>
              </MDBox>
            )}
            <Configurator />
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(filteredRoutes)}
          <Route path="/reset-password" element={<ResetIllustration />} />
          <Route path="/authentication/reset-password/" element={<ResetIllustration />} />
          <Route path="*" element={<Navigate to="/dashboards" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && checkAuth() && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandIcon={(transparentSidenav && !darkMode) || whiteSidenav ? brandIconDark : brandIconLight}
            brandName=""
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          {isNarrow && miniSidenav && (
            <MDBox
              sx={{
                position: "fixed",
                right: 16,
                bottom: 16,
                zIndex: (theme) => theme.zIndex.modal + 1,
              }}
            >
              <IconButton
                id="sidenav-popup-button"
                onClick={() => setMiniSidenav(dispatch, false)}
                size="large"
                sx={{
                  bgcolor: "#bababa",
                  color: "black",
                  boxShadow: 3,
                  "&:hover": { bgcolor: "#a0a0a0" },
                }}
              >
                <Icon>menu</Icon>
              </IconButton>
            </MDBox>
          )}
          <Configurator />
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(filteredRoutes)}
        <Route path="/reset-password" element={<ResetIllustration />} />
        <Route path="/sign-in" element={<SignInIllustration />} />
        <Route path="*" element={<Navigate to={checkAuth() ? "/dashboards" : "/sign-in"} />} />
      </Routes>
    </ThemeProvider>
  );
};

export default App;
